import {
  Card,
  Button,
  Flex,
  Typography,
  Select,
  message,
  Divider,
  Tag,
  Space,
  Segmented,
} from "antd";
import {
  collection,
  getDocs,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { db_fire } from "../firebase";
import moment from "moment";
import { ArrowLeftOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;
const { Option } = Select;

/* 🔄 STATUS FLOWS */
const DELIVERY_STATUS_FLOW = [
  "ACCEPTED",
  "READY",
  "PICKED",
  "DELIVERED"
];

const TAKEAWAY_STATUS_FLOW = [
  "ACCEPTED",
  "READY",
  "DELIVERED",
];

export default function Order() {
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate()
  const [filterOrderType, setFilterOrderType] = useState("TAKEAWAY");
  const [filter, setfilter] = useState("ALL");

  /* 🔥 REALTIME ORDERS */

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db_fire, "orders"),
      async (snap) => {

        // 1️⃣ Get all orders
        const ordersData = snap.docs.map(d => ({
          id: d.id,
          ...d.data(),
        }));

        // 2️⃣ Extract unique userIds
        const userIds = [
          ...new Set(
            ordersData
              .map(o => o.userId)
              .filter(Boolean)
          ),
        ];

        // 3️⃣ Fetch all users ONCE
        const usersSnap = await getDocs(collection(db_fire, "users"));

        const usersMap = {};
        usersSnap.forEach(doc => {
          usersMap[doc.id] = doc.data();
        });

        // 4️⃣ Attach user details to orders
        const finalOrders = ordersData.map(order => ({
          ...order,
          user: usersMap[order.userId] || null,
        }));

        setOrders(finalOrders);
      }
    );

    return () => unsub();
  }, []);



  /* ✅ ACCEPT ORDER */
  const acceptOrder = async (order) => {
    let updatePayload = {
      adminStatus: "ACCEPTED",
      deliveryStatus: "PENDING",
      updatedAt: serverTimestamp(),
    };

    await updateDoc(doc(db_fire, "orders", order.id), updatePayload);
    message.success("Order accepted");
  };

  /* ✅ ACCEPT ORDER */
  const updateOrderStatus = async (order, status) => {
    let updatePayload = {
      adminStatus: status,
      updatedAt: serverTimestamp(),
    };

    if (status === "CANCELLED") {
      updatePayload.adminStatus = "CANCELLED"
      updatePayload.deliveryStatus = "CANCELLED"
      updatePayload.deliveryPartnerId = null
    }

    await updateDoc(doc(db_fire, "orders", order.id), updatePayload);
    message.success("Order updated");
  };


  return (
    <>
      <Flex>
        <Button
          shape='round'
          size='small'
          style={{ padding: "10px 15px", border: "none" }}
          type={filterOrderType === "TAKEAWAY" ? "primary" : "default"}
          onClick={_ => setFilterOrderType("TAKEAWAY")}>TAKEAWAY</Button>
        <Button
          shape='round'
          size='small'
          style={{ padding: "10px 15px", border: "none" }}
          type={filterOrderType === "DELIVERY" ? "primary" : "default"}
          onClick={_ => setFilterOrderType("DELIVERY")}>DELIVERY</Button>
      </Flex>

      <Flex style={{ margin: "15px 0px", overflowX: "auto" }} className="scroll-bar-none">
        <Button
          shape='round'
          size='small'
          style={{ padding: "10px 15px", border: "none" }}
          type={filter === "ALL" ? "primary" : "default"}
          onClick={_ => setfilter("ALL")}>ALL</Button>

        <Button
          shape='round'
          size='small'
          style={{ padding: "10px 15px", border: "none" }}
          type={filter === "PENDING" ? "primary" : "default"}
          onClick={_ => setfilter("PENDING")}>PENDING</Button>

        {
          filterOrderType === "TAKEAWAY"
            ? TAKEAWAY_STATUS_FLOW.map(e => <Button
              shape='round'
              size='small'
              style={{ padding: "10px 15px", border: "none" }}
              type={filter === e ? "primary" : "default"}
              onClick={_ => setfilter(e)}>{e.replaceAll("_", " ")}</Button>)
            : DELIVERY_STATUS_FLOW.map(e => <Button
              shape='round'
              size='small'
              style={{ padding: "10px 15px", border: "none" }}
              type={filter === e ? "primary" : "default"}
              onClick={_ => setfilter(e)}>{e.replaceAll("_", " ")}</Button>)
        }

      </Flex>

      {orders
        .filter(f => f.orderType.toLowerCase() === filterOrderType.toLowerCase())
        .filter(f => filter === "ALL" ? f : f.adminStatus === filter)
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((o) => {
          const isDelivery = o.orderType === "delivery";
          const statusFlow = isDelivery
            ? DELIVERY_STATUS_FLOW
            : TAKEAWAY_STATUS_FLOW;

          return (
            <Card
              key={o.id}
              size="small"
              style={{
                // background:"url('/image/paid.png') 10% 40% fit",
                marginBottom: 12,
                filter: `grayscale(${["DELIVERED", "COMPLETED"].includes(o.adminStatus) ? 1 : 0})`,
              }}
            >
              <Flex vertical gap={10}>
                {/* 🧾 ORDER INFO */}
                <Flex justify="space-between">
                  <Flex vertical>
                    <Space>
                      <Text strong>Order No. <Text mark>{o.id.slice(0, 3)}</Text></Text>
                      <big>•</big>
                      <Text italic>{moment(o.createdAt?.toDate()).fromNow()}</Text>
                    </Space>
                    <Space>
                      <Text>{o.user?.name || "Customer"}</Text>
                      <big>•</big>
                      <Text>{o.items.length} Item{o.items.length > 1 ? "s" : ""}</Text>
                      <big>•</big>
                      <Text>₹{o.finalAmount}</Text>
                    </Space>
                    {o.deliveryLocation && <Text type="secondary" italic>
                      <EnvironmentOutlined />   {o.deliveryLocation?.address || "Delivery Address"}
                    </Text>}
                  </Flex>
                  {o.payment && <img src="/image/paid.png" height={50} alt="error" />}
                </Flex>

                {/* 🎛 ACTIONS */}
                <Flex gap={8} align="center">
                  {o.adminStatus === "PENDING" && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => acceptOrder(o)}
                    >
                      ACCEPT
                    </Button>
                  )}

                  {o.adminStatus === "ACCEPTED" && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => updateOrderStatus(o, "READY")}
                    >
                      READY
                    </Button>
                  )}

                  {o.adminStatus === "READY" && filterOrderType === "TAKEAWAY" && (
                    <Button
                      color="red"
                      type="primary"
                      size="small"
                      onClick={() => updateOrderStatus(o, "DELIVERED")}
                    >
                      DELIVERED
                    </Button>
                  )}

                  {["ACCEPTED", "PENDING"].includes(o.adminStatus) && (
                    <Button
                      danger
                      type="primary"
                      size="small"
                      onClick={() => updateOrderStatus(o, "CANCELLED")}
                    >
                      CANCEL
                    </Button>
                  )}
                  <Tag color={isDelivery ? "blue" : "green"}>
                    {o.orderType.toUpperCase()}
                  </Tag>
                  {filter === "ALL" &&
                    <Tag style={{ marginLeft: "-7px" }} color={"cyan"}>
                      {o.adminStatus}
                    </Tag>}
                </Flex>
              </Flex>
            </Card>
          );
        })}
    </>
  );
}
